/* eslint-disable react/prop-types */
import React from "react";

/**
 * ProposalSectionRenderer — switchboard that dispatches a `section` object
 * (kind + content) to the right visual block. Adding a new section kind
 * later is a one-case addition here plus an entry in the builder UI.
 *
 * All visual rules live in proposalTheme.css; this file just composes
 * semantic markup so the page is easy to skim and PDF-friendly.
 */

const HeroSection = ({ section, proposal }) => {
  const c = section.content || {};
  return (
    <section className="miq-section" data-section-kind="hero">
      <div className="miq-section--wide">
        {c.eyebrow && <div className="eyebrow" style={{ marginBottom: 18 }}>{c.eyebrow}</div>}
        <h1 className="serif miq-h1" data-testid="proposal-hero-headline">
          {c.headline || proposal.title}
        </h1>
        {c.subheadline && (
          <p className="miq-body" style={{ marginTop: 18, maxWidth: 640 }}>
            {c.subheadline}
          </p>
        )}
        <div className="miq-hr" style={{ marginTop: 28, maxWidth: 200 }} />
      </div>
    </section>
  );
};

const ClientInfoSection = ({ section, proposal }) => {
  const lead = proposal.client_snapshot || {};
  const rows = [
    ["Name", lead.name],
    ["Email", lead.email],
    ["Phone", lead.phone],
    ["Country", lead.country],
    ["Service", lead.service],
    ["Reference", lead.ref_id || proposal.lead_reference_id],
  ].filter(([, v]) => v);
  return (
    <section className="miq-section" data-section-kind="client_info">
      <div className="miq-section--wide">
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Prepared For"}</div>
        <div className="miq-glass miq-card">
          <div className="miq-grid-2" style={{ rowGap: 14 }}>
            {rows.map(([label, value]) => (
              <div key={label}>
                <div className="miq-small">{label}</div>
                <div style={{ marginTop: 4, fontWeight: 500 }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const OpportunitySection = ({ section }) => {
  const c = section.content || {};
  return (
    <section className="miq-section" data-section-kind="opportunity">
      <div className="miq-section--narrow">
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Overview"}</div>
        <h2 className="serif miq-h2">{c.headline || "The Opportunity"}</h2>
        {c.summary && <p className="miq-body" style={{ marginTop: 20 }}>{c.summary}</p>}
        {Array.isArray(c.highlights) && c.highlights.length > 0 && (
          <ul style={{ marginTop: 24, paddingLeft: 0, listStyle: "none" }}>
            {c.highlights.map((h, i) => (
              <li key={i} className="miq-body" style={{ paddingLeft: 24, position: "relative", marginBottom: 10 }}>
                <span className="accent" style={{ position: "absolute", left: 0, top: 0 }}>•</span> {h}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

const InvestmentSection = ({ section }) => {
  const c = section.content || {};
  return (
    <section className="miq-section" data-section-kind="investment">
      <div className="miq-section--narrow">
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Investment Details"}</div>
        <div className="miq-glass miq-card">
          <div className="miq-grid-2">
            <div>
              <div className="miq-small">{c.amount_label || "Amount"}</div>
              <div className="serif accent" style={{ fontSize: 36, marginTop: 6, letterSpacing: "-0.02em" }}>
                {c.amount_value || "—"}
              </div>
            </div>
            <div>
              <div className="miq-small">{c.service_label || "Service"}</div>
              <div className="serif" style={{ fontSize: 28, marginTop: 6 }}>{c.service_value || "—"}</div>
            </div>
          </div>
          {c.notes && (
            <p className="miq-small" style={{ marginTop: 24, lineHeight: 1.7 }}>{c.notes}</p>
          )}
        </div>
      </div>
    </section>
  );
};

const BenefitsSection = ({ section }) => {
  const items = Array.isArray(section.content?.items) ? section.content.items : [];
  return (
    <section className="miq-section" data-section-kind="benefits">
      <div className="miq-section--wide">
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Benefits & Advantages"}</div>
        <h2 className="serif miq-h2" style={{ marginBottom: 32 }}>What you receive</h2>
        <div className="miq-grid-4">
          {items.map((it, i) => (
            <div key={i} className="miq-glass miq-card">
              <div className="serif" style={{ fontSize: 20, marginBottom: 10 }}>{it.title}</div>
              <div className="miq-body" style={{ fontSize: 14 }}>{it.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PaymentPlanSection = ({ section }) => {
  const rows = Array.isArray(section.content?.rows) ? section.content.rows : [];
  return (
    <section className="miq-section" data-section-kind="payment_plan">
      <div className="miq-section--wide">
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Payment Plan"}</div>
        <h2 className="serif miq-h2" style={{ marginBottom: 32 }}>Structured for flexibility</h2>
        <div className="miq-grid-4">
          {rows.map((r, i) => (
            <div key={i} className="miq-glass miq-card">
              <div className="miq-pct">{r.amount}</div>
              <div style={{ marginTop: 14, fontWeight: 600 }}>{r.label}</div>
              <div className="miq-small" style={{ marginTop: 6 }}>{r.note}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TimelineSection = ({ section }) => {
  const steps = Array.isArray(section.content?.steps) ? section.content.steps : [];
  return (
    <section className="miq-section" data-section-kind="timeline">
      <div className="miq-section--wide">
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Timeline"}</div>
        <h2 className="serif miq-h2" style={{ marginBottom: 32 }}>Process overview</h2>
        <div className="miq-grid-4">
          {steps.map((s, i) => (
            <div key={i} className="miq-glass miq-card">
              <div className="miq-step-no">Step {i + 1}</div>
              <div className="serif" style={{ fontSize: 20, marginTop: 12 }}>{s.title}</div>
              <div className="miq-body" style={{ marginTop: 8, fontSize: 14 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyUsSection = ({ section, proposal }) => {
  const points = Array.isArray(section.content?.points) ? section.content.points : [];
  const partner = (proposal.branding_snapshot && proposal.branding_snapshot.partner_name)
    ? proposal.branding_snapshot.partner_name
    : "your partner";
  return (
    <section className="miq-section" data-section-kind="why_us">
      <div className="miq-section--narrow">
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Why us"}</div>
        <h2 className="serif miq-h2">
          MoveAbroadIQ <span className="accent">in collaboration with</span> {partner}
        </h2>
        <ul style={{ marginTop: 24, paddingLeft: 0, listStyle: "none" }}>
          {points.map((p, i) => (
            <li key={i} className="miq-body" style={{ paddingLeft: 24, position: "relative", marginBottom: 12 }}>
              <span className="accent" style={{ position: "absolute", left: 0 }}>—</span> {p}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const NotesSection = ({ section }) =>
  !section.content?.body ? null : (
    <section className="miq-section" data-section-kind="notes">
      <div className="miq-section--narrow">
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Notes"}</div>
        <div className="miq-glass miq-card miq-body" style={{ whiteSpace: "pre-wrap" }}>
          {section.content.body}
        </div>
      </div>
    </section>
  );

const CtaSection = ({ section, proposal }) => {
  const c = section.content || {};
  const brand = proposal.branding_snapshot || {};
  const email = brand.company_contact_email;
  return (
    <section className="miq-section" data-section-kind="cta">
      <div className="miq-section--narrow" style={{ textAlign: "center" }}>
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Next Steps"}</div>
        <h2 className="serif miq-h2">{c.headline || "Ready to move forward?"}</h2>
        {c.subheadline && (
          <p className="miq-body" style={{ marginTop: 16, maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>
            {c.subheadline}
          </p>
        )}
        {email && (
          <a className="miq-btn-primary miq-no-print"
             style={{ display: "inline-block", marginTop: 28, textDecoration: "none" }}
             href={`mailto:${email}`}>
            Reply to your advisor
          </a>
        )}
      </div>
    </section>
  );
};

const FooterSection = ({ proposal }) => {
  const brand = proposal.branding_snapshot || {};
  const partner = brand.partner_name ? ` in collaboration with ${brand.partner_name}` : "";
  return (
    <footer className="miq-footer">
      Prepared by MoveAbroadIQ{partner}.{" "}
      {brand.company_address ? <span style={{ opacity: 0.7 }}> · {brand.company_address}</span> : null}
    </footer>
  );
};

const CustomSection = ({ section }) => {
  const c = section.content || {};
  return (
    <section className="miq-section" data-section-kind="custom">
      <div className="miq-section--narrow">
        <div className="eyebrow" style={{ marginBottom: 18 }}>{section.title || "Section"}</div>
        {c.headline && <h2 className="serif miq-h2">{c.headline}</h2>}
        {c.body && (
          <p className="miq-body" style={{ marginTop: 18, whiteSpace: "pre-wrap" }}>{c.body}</p>
        )}
      </div>
    </section>
  );
};

const RENDERERS = {
  hero: HeroSection,
  client_info: ClientInfoSection,
  opportunity: OpportunitySection,
  investment: InvestmentSection,
  benefits: BenefitsSection,
  payment_plan: PaymentPlanSection,
  timeline: TimelineSection,
  why_us: WhyUsSection,
  notes: NotesSection,
  cta: CtaSection,
  footer: FooterSection,
  custom: CustomSection,
};

export default function ProposalSectionRenderer({ section, proposal }) {
  if (!section || section.hidden) return null;
  const Cmp = RENDERERS[section.kind] || CustomSection;
  return <Cmp section={section} proposal={proposal} />;
}
